import React, { useState, forwardRef, useImperativeHandle } from "react";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export type CustomPaginationType = {
  setPagination: (args: {
    skip?: number;
    take?: number;
    totalCount?: number;
    hasNextPage?: boolean;
  }) => void;
};

interface Props {
  onPaginationChange: (args: { skip: number; take: number }) => void;
}

const CustomPagination = forwardRef<CustomPaginationType, Props>(
  (props, ref) => {
    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    useImperativeHandle(ref, () => ({
      setPagination({
        skip,
        take,
        totalCount,
        hasNextPage,
      }: {
        skip?: number;
        take?: number;
        totalCount?: number;
        hasNextPage?: boolean;
      }) {
        if (skip !== undefined) setSkip(skip);
        if (take !== undefined) setTake(take);
        if (totalCount !== undefined) setTotalCount(totalCount);
        if (hasNextPage !== undefined) setHasNextPage(hasNextPage);
      },
    }));

    const updatePagination = async (args: { skip: number; take: number }) => {
      setSkip(args.skip);
      setTake(args.take);
      await props.onPaginationChange(args);
    };

    let lastpage = 0;
    if (totalCount) {
      lastpage = Math.ceil(totalCount / take);
    }

    let currentPage = 1;
    if (skip !== 0) currentPage = skip / take + 1;

    const pagesToDisplay = [];
    pagesToDisplay.push(currentPage);
    for (let i = 1; i < 5; i++) {
      if (lastpage && currentPage + i < lastpage)
        pagesToDisplay.push(currentPage + i);
      if (currentPage - i > 0) pagesToDisplay.push(currentPage - i);
    }
    pagesToDisplay.sort((a, b) => a - b);

    // if pagesToDisplay is longer than 5, remove the element furthest from the current page
    while (pagesToDisplay.length > 5) {
      if (
        currentPage - pagesToDisplay[0] <
        pagesToDisplay[pagesToDisplay.length - 1] - currentPage
      ) {
        pagesToDisplay.pop();
      } else {
        pagesToDisplay.shift();
      }
    }

    if (!take) return null;

    return (
      <nav className="flex justify-center">
        <ul className="flex items-center gap-1 text-sm">
          {pagesToDisplay.indexOf(1) === -1 ? (
            <li key={1}>
              <button
                className="page-link inline-flex items-center justify-center rounded px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                onClick={() => {
                  updatePagination({
                    skip: 0,
                    take,
                  });
                }}
              >
                1
              </button>
            </li>
          ) : null}

          {currentPage > 2 && pagesToDisplay.indexOf(2) === -1 ? (
            <li>
              <span className="inline-flex items-center justify-center px-2">
                <MoreHorizontal className="h-4 w-4 text-gray-400" />
              </span>
            </li>
          ) : null}

          {pagesToDisplay.map((page) => (
            <li key={page}>
              <button
                className={cn(
                  "page-link inline-flex items-center justify-center rounded px-3 py-1.5 border transition-colors",
                  page === currentPage
                    ? "active bg-[#0b6477] border-[#0b6477] text-white"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                )}
                onClick={() => {
                  updatePagination({
                    skip: (page - 1) * take,
                    take,
                  });
                }}
              >
                {page}
              </button>
            </li>
          ))}

          {totalCount &&
          pagesToDisplay.indexOf(lastpage - 1) === -1 &&
          currentPage < lastpage - 1 ? (
            <li>
              <span className="inline-flex items-center justify-center px-2">
                <MoreHorizontal className="h-4 w-4 text-gray-400" />
              </span>
            </li>
          ) : null}
          {totalCount && pagesToDisplay.indexOf(lastpage) === -1 ? (
            <li key={lastpage}>
              <button
                className="page-link inline-flex items-center justify-center rounded px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                onClick={() => {
                  updatePagination({
                    skip: (lastpage - 1) * take,
                    take,
                  });
                }}
              >
                {lastpage}
              </button>
            </li>
          ) : null}
          {!totalCount && hasNextPage ? (
            <li key={"next"}>
              <button
                className="page-link inline-flex items-center justify-center rounded px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                onClick={() => {
                  updatePagination({
                    skip: currentPage * take,
                    take,
                  });
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
    );
  }
);

export default CustomPagination;
